import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Leaf, Loader2, Mail, CheckCircle2, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [simulated, setSimulated] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await api.post('/auth/forgot-password', { email: email.trim().toLowerCase() });
      setSent(true);
      if (res.data?.simulated) {
        setSimulated({ link: res.data.reset_link, token: res.data.token });
      }
    } catch (e) {
      // Backend returns 200 generic; only 400 if empty email
      toast.error(e?.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    if (!simulated) return;
    navigator.clipboard.writeText(simulated.link);
    setCopied(true);
    toast.success(t('forgotPassword.linkCopied'));
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary">
            <Leaf className="w-5 h-5 text-primary-foreground" />
          </div>
          <span className="font-heading font-bold text-2xl tracking-tight">{t('app.name')}</span>
        </div>

        <Card className="p-8 shadow-lg border-border">
          <div className="mb-6">
            <h1 className="text-2xl font-bold tracking-tight">{t('forgotPassword.title')}</h1>
            <p className="text-sm text-muted-foreground mt-1">{t('forgotPassword.subtitle')}</p>
          </div>

          {!sent ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">{t('auth.email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 rtl:left-auto rtl:right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="email" data-testid="forgot-email-input" type="email" value={email}
                         onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com"
                         className="pl-9 rtl:pl-3 rtl:pr-9" required />
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full" data-testid="forgot-submit-btn">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('forgotPassword.sending')}</> : t('forgotPassword.sendLink')}
              </Button>
            </form>
          ) : (
            <div className="space-y-4" data-testid="forgot-sent-panel">
              <div className="flex items-start gap-2 p-3 rounded-md bg-green-500/10 border border-green-500/30 text-sm">
                <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
                <span>{t('forgotPassword.emailSent')}</span>
              </div>

              {simulated && (
                <div className="p-3 rounded-md bg-amber-500/10 border border-amber-500/30 text-xs space-y-2" data-testid="simulated-link-panel">
                  <p className="font-medium text-amber-900 dark:text-amber-200">{t('forgotPassword.simulatedNotice')}</p>
                  <div className="flex items-center gap-2 bg-muted/50 border border-border rounded p-2 font-mono text-[10px] break-all">
                    <span className="flex-1" data-testid="simulated-link-value">{simulated.link}</span>
                    <Button variant="ghost" size="sm" onClick={copyLink} data-testid="copy-reset-link-btn">
                      {copied ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
                    </Button>
                  </div>
                  <Link to={`/reset-password?token=${simulated.token}`}
                        className="inline-block text-primary hover:underline" data-testid="goto-reset-link">
                    {t('resetPassword.reset')} →
                  </Link>
                </div>
              )}
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-border text-center text-sm">
            <Link to="/login" className="text-primary hover:underline font-medium" data-testid="back-to-signin-link">
              ← {t('forgotPassword.backToSignIn')}
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
